import React, { createContext, useContext, useRef, ReactNode } from 'react';
import { getDeviceOptimizedSettings } from '../utils/deviceOptimization';

interface PendingRequest<T> {
  promise: Promise<T>;
  timestamp: number;
}

interface RequestCacheContextType {
  getCachedRequest: <T>(key: string, fetcher: () => Promise<T>, ttl?: number) => Promise<T>;
  clearCache: (keyPattern?: string) => void;
  clearExpiredCache: () => void;
}

const RequestCacheContext = createContext<RequestCacheContextType | undefined>(undefined);

interface RequestCacheProviderProps {
  children: ReactNode;
}

export const RequestCacheProvider: React.FC<RequestCacheProviderProps> = ({ children }) => {
  const cache = useRef<Map<string, { data: unknown; timestamp: number }>>(new Map());
  const pendingRequests = useRef<Map<string, PendingRequest<unknown>>>(new Map());

  const getCachedRequest = async <T,>(
    key: string, 
    fetcher: () => Promise<T>, 
    ttl?: number // TTL will be device-optimized if not provided
  ): Promise<T> => {
    // Use device-optimized TTL if not specified
    const effectiveTTL = ttl ?? getDeviceOptimizedSettings().cacheTTL;
    const now = Date.now();
    
    // Check if we have a pending request for this key
    const pending = pendingRequests.current.get(key) as PendingRequest<T> | undefined;
    if (pending) {
      console.log(`🔄 Request deduplication: Using pending request for "${key}"`);
      return pending.promise;
    }

    // Check cache first
    const cached = cache.current.get(key);
    if (cached && (now - cached.timestamp) < effectiveTTL) {
      console.log(`📋 Cache hit: Using cached data for "${key}" (TTL: ${effectiveTTL}ms)`);
      return cached.data as T;
    }

    console.log(`🌐 Cache miss: Making new request for "${key}"`);
    
    // Create new request
    const promise = fetcher().then((data) => {
      // Store in cache
      cache.current.set(key, { data, timestamp: now });
      // Remove from pending
      pendingRequests.current.delete(key);
      return data;
    }).catch((error) => {
      // Remove from pending on error
      pendingRequests.current.delete(key);
      throw error;
    });

    // Store as pending
    pendingRequests.current.set(key, { promise, timestamp: now } as PendingRequest<unknown>);
    
    return promise;
  };

  const clearCache = (keyPattern?: string) => {
    if (keyPattern) {
      const regex = new RegExp(keyPattern);
      for (const [key] of cache.current) {
        if (regex.test(key)) {
          cache.current.delete(key);
        }
      }
      console.log(`🗑️ Cache cleared for pattern: ${keyPattern}`);
    } else {
      cache.current.clear();
      pendingRequests.current.clear();
      console.log(`🗑️ All cache cleared`);
    }
  };

  const clearExpiredCache = () => {
    const now = Date.now();
    let cleared = 0;
    
    for (const [key, value] of cache.current) {
      if (now - value.timestamp > 300000) { // 5 minutes
        cache.current.delete(key);
        cleared++;
      }
    }
    
    // Clear old pending requests (older than 2 minutes)
    for (const [key, value] of pendingRequests.current) {
      if (now - value.timestamp > 120000) {
        pendingRequests.current.delete(key);
        cleared++;
      }
    }
    
    if (cleared > 0) {
      console.log(`🗑️ Cleared ${cleared} expired cache entries`);
    }
  };

  // Auto-cleanup every 5 minutes
  React.useEffect(() => {
    const interval = setInterval(clearExpiredCache, 300000);
    return () => clearInterval(interval);
  }, []);

  const value: RequestCacheContextType = {
    getCachedRequest,
    clearCache,
    clearExpiredCache,
  };

  return (
    <RequestCacheContext.Provider value={value}>
      {children}
    </RequestCacheContext.Provider>
  );
};

export const useRequestCache = (): RequestCacheContextType => {
  const context = useContext(RequestCacheContext);
  if (!context) {
    throw new Error('useRequestCache must be used within a RequestCacheProvider');
  }
  return context;
};
